/* eslint-disable */
import React, { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import useLogout from "../../hooks/useLogout";
import { capitalizeFirstLetter } from "../../services/util";

// ---------- NAV ITEM ----------
function NavItem({ to, icon, label, isFullScreen, loggingOut }) {
  const location = useLocation();
  const [hover, setHover] = useState(false);

  // Active check
  const isActive =
    location.pathname === to ||
    (to !== "/dashboard" && location.pathname.startsWith(to));

  return (
    <motion.div
      className="relative group"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      layout
      whileHover={{ scale: 1.05 }}
    >
      <NavLink
        to={loggingOut ? "" : to}
        className={`flex items-center gap-4 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200
          ${isActive
            ? "bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-lg"
            : "text-white hover:text-white hover:bg-white/10"
          }`}
      >
        <span className="material-icons-outlined">{icon}</span>
        {!isFullScreen && <span>{label}</span>}
      </NavLink>

      {/* Tooltip for collapsed mode */}
      <AnimatePresence>
        {hover && isFullScreen && (
          <motion.div
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -10 }}
            className="absolute left-full top-1/2 -translate-y-1/2 ml-2 px-3 py-1 rounded-md bg-gray-900 text-white text-sm shadow-lg whitespace-nowrap z-50"
          >
            {label}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// ---------- NAV DROPDOWN ----------
function NavDropdown({ icon, label, children, isFullScreen }) {
  const [open, setOpen] = useState(false);
  const [hover, setHover] = useState(false);

  return (
    <div
      className="relative"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <button
        onClick={() => setOpen(!open)}
        className={`flex items-center justify-between w-full px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200
          text-white hover:text-white hover:bg-white/10`}
      >
        <div className="flex items-center gap-4">
          <span className="material-icons-outlined">{icon}</span>
          {!isFullScreen && <span>{label}</span>}
        </div>
        {!isFullScreen && (
          <motion.span
            animate={{ rotate: open ? 90 : 0 }}
            transition={{ type: "spring", stiffness: 300, damping: 20 }}
            className="material-icons-outlined transition-transform"
          >
            chevron_right
          </motion.span>
        )}
      </button>

      {/* Tooltip for collapsed mode */}
      <AnimatePresence>
        {hover && isFullScreen && (
          <motion.div
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -10 }}
            className="absolute left-full top-1/2 -translate-y-1/2 ml-2 px-3 py-1 rounded-md bg-gray-900 text-white text-sm shadow-lg whitespace-nowrap z-50"
          >
            {label}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Dropdown items */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ type: "spring", stiffness: 300, damping: 25 }}
            className="pl-12 mt-1 space-y-1 overflow-hidden"
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ---------- DASHBOARD NAVIGATION ----------
function DashboardNavigation({ isFullScreen }) {
  const [loggingOut, setLoggingOut] = useState(false);
  const [handleLogout] = useLogout(setLoggingOut);
  const userState = { market: "market" };

  return (
    <motion.nav
      className={`h-screen bg-success-800 backdrop-blur-xl border-r border-success-800 flex flex-col py-4 space-y-2 px-2 transition-all duration-300 ${
        isFullScreen ? "w-16" : "w-64"
      }`}
      layout
    >
      {/* MAIN */}
      <NavItem to="/dashboard" icon="dashboard" label="Dashboard" isFullScreen={isFullScreen} loggingOut={loggingOut} />
      <NavItem to="/dashboard/community-managers" icon="groups" label="Community Managers" isFullScreen={isFullScreen} loggingOut={loggingOut} />
      <NavItem to="/dashboard/cohatch-documents" icon="folder_shared" label="Documents" isFullScreen={isFullScreen} loggingOut={loggingOut} />
      <NavItem to="/dashboard/administrator" icon="admin_panel_settings" label="Administrators" isFullScreen={isFullScreen} loggingOut={loggingOut} />

      {/* IMPACT */}
      <NavDropdown icon="insights" label="Impact" isFullScreen={isFullScreen}>
        <NavItem to="/dashboard/locations" icon="layers" label="All Locations" isFullScreen={isFullScreen} loggingOut={loggingOut} />
        <NavItem to={`/dashboard/marketlevel/${userState.market}`} icon="query_stats" label={capitalizeFirstLetter(userState.market)} isFullScreen={isFullScreen} loggingOut={loggingOut} />
        <NavItem to="/dashboard/leaderboard" icon="analytics" label="Leaderboard" isFullScreen={isFullScreen} loggingOut={loggingOut} />
        <NavItem to="/dashboard/all_applications" icon="person_search" label="All Applications" isFullScreen={isFullScreen} loggingOut={loggingOut} />
      </NavDropdown>

      {/* TOOLS */}
      <NavItem to="/dashboard/technical-support" icon="support_agent" label="Technical Support" isFullScreen={isFullScreen} loggingOut={loggingOut} />
      <NavItem to="/dashboard/print-transactions" icon="print" label="Pharos" isFullScreen={isFullScreen} loggingOut={loggingOut} />
      <NavItem to="/dashboard/salesforce-cohatch-compare" icon="compare_arrows" label="OfficeRnd & Salesforce" isFullScreen={isFullScreen} loggingOut={loggingOut} />
      <NavItem to="/dashboard/settings" icon="settings" label="Settings" isFullScreen={isFullScreen} loggingOut={loggingOut} />

      {/* LOGOUT */}
      <div
        className="mt-auto flex items-center gap-4 px-4 py-3 text-white hover:text-white hover:bg-red-500/50 rounded-xl cursor-pointer transition-all duration-200"
        onClick={handleLogout}
      >
        <span className="material-icons-outlined">logout</span>
        {!isFullScreen && "Logout"}
      </div>
    </motion.nav>
  );
}

export default DashboardNavigation;